import * as api from '~/api';

export type PipelineSaveConflictKind =
  'already-draft' | 'duplicate-version' | 'deployed-cannot-update' | 'unknown';

export type PipelineSaveConflictResolution = {
  kind: PipelineSaveConflictKind;
  messageKey: string;
  messageParams?: { version: string };
};

type HistoryVersion = {
  id?: string | null;
  draft?: boolean | null;
  disabled?: boolean | null;
  versionMajor?: number | null;
  versionMinor?: number | null;
};

type ResolvePipelineSaveConflictParams = {
  error: unknown;
  realm: string;
  pipelineName: string;
  versionMajor?: number;
};

const DRAFT_VERSION_PATTERN =
  /existing pipeline version in draft mode:\s*(\d+)\.(\d+)/i;
const DUPLICATE_VERSION_PATTERN = /with version\s+(\d+)\.(\d+)/i;

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

const versionedMessage = (
  withVersionKey: string,
  withoutVersionKey: string,
  version?: string
): Pick<PipelineSaveConflictResolution, 'messageKey' | 'messageParams'> =>
  version
    ? { messageKey: withVersionKey, messageParams: { version } }
    : { messageKey: withoutVersionKey };

export const classifyPipelineSaveConflict = (
  error: unknown
): PipelineSaveConflictKind => {
  const message = getErrorMessage(error).toLowerCase();

  if (message.includes('already is a pipeline in draft mode')) {
    return 'already-draft';
  }

  if (message.includes('already exists with the same name and version')) {
    return 'duplicate-version';
  }

  if (message.includes('deployed pipeline cannot be updated')) {
    return 'deployed-cannot-update';
  }

  return 'unknown';
};

export const parseConflictVersion = (error: unknown): string | undefined => {
  const message = getErrorMessage(error);
  const draftMatch = DRAFT_VERSION_PATTERN.exec(message);
  if (draftMatch) {
    return `${draftMatch[1]}.${draftMatch[2]}`;
  }

  const duplicateMatch = DUPLICATE_VERSION_PATTERN.exec(message);
  if (duplicateMatch) {
    return `${duplicateMatch[1]}.${duplicateMatch[2]}`;
  }

  return undefined;
};

const formatVersion = (
  versionMajor?: number | null,
  versionMinor?: number | null
): string | undefined => {
  if (versionMajor == null || versionMinor == null) {
    return undefined;
  }

  return `${versionMajor}.${versionMinor}`;
};

type DraftLookup = { found: false } | { found: true; version?: string };

const selectExistingDraft = (versions: HistoryVersion[]): DraftLookup => {
  const drafts = versions
    .filter(
      (version): version is HistoryVersion & { id: string } =>
        Boolean(version.id) &&
        version.draft === true &&
        version.disabled !== true
    )
    .sort(
      (left, right) => (right.versionMinor ?? 0) - (left.versionMinor ?? 0)
    );

  const draft = drafts[0];
  if (!draft) {
    return { found: false };
  }

  return {
    found: true,
    version: formatVersion(draft.versionMajor, draft.versionMinor)
  };
};

const fetchExistingDraft = async ({
  realm,
  pipelineName,
  versionMajor
}: Required<
  Omit<ResolvePipelineSaveConflictParams, 'error'>
>): Promise<DraftLookup> => {
  const { data } = await api.pipelineHistory.fetch(
    {
      realm,
      pipelineName,
      versionMajor: String(versionMajor)
    },
    { fetchPolicy: 'network-only' }
  );

  return selectExistingDraft(data?.pipelineHistory ?? []);
};

const draftExistsResolution = (
  kind: 'already-draft',
  version?: string
): PipelineSaveConflictResolution => ({
  kind,
  ...versionedMessage(
    'label.pipeline_save_conflict_draft_exists',
    'label.pipeline_save_conflict_draft_exists_no_version',
    version
  )
});

export const resolvePipelineSaveConflict = async ({
  error,
  realm,
  pipelineName,
  versionMajor
}: ResolvePipelineSaveConflictParams): Promise<PipelineSaveConflictResolution> => {
  const kind = classifyPipelineSaveConflict(error);
  const parsedVersion = parseConflictVersion(error);

  if (kind === 'duplicate-version') {
    return {
      kind,
      ...versionedMessage(
        'label.pipeline_save_conflict_duplicate_version',
        'label.pipeline_save_conflict_duplicate_version_no_version',
        parsedVersion
      )
    };
  }

  if (kind === 'deployed-cannot-update') {
    return {
      kind,
      messageKey: 'label.pipeline_save_conflict_deployed'
    };
  }

  if (kind !== 'already-draft') {
    return {
      kind,
      messageKey: 'label.update_pipeline_msg_error'
    };
  }

  if (versionMajor == null) {
    return draftExistsResolution(kind, parsedVersion);
  }

  try {
    const draft = await fetchExistingDraft({
      realm,
      pipelineName,
      versionMajor
    });

    if (draft.found) {
      return draftExistsResolution(kind, draft.version ?? parsedVersion);
    }

    return {
      kind,
      messageKey: 'label.pipeline_save_conflict_inconsistent',
      ...(parsedVersion ? { messageParams: { version: parsedVersion } } : {})
    };
  } catch {
    return draftExistsResolution(kind, parsedVersion);
  }
};
